import { useState } from 'react';
import { supabase } from '../lib/supabase';
import { Mail, Loader2 } from 'lucide-react';

type ReminderTestResult =
  | {
      success: true;
      data: unknown;
    }
  | {
      success: false;
      error: string;
      details?: unknown;
      responseData?: unknown;
    };

export default function TestReminderEmail() {
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<ReminderTestResult | null>(null);

  const testReminderFunction = async () => {
    setTesting(true);
    setResult(null);

    try {
      const { data, error } = await supabase.functions.invoke('send-onboarding-reminders', {
        body: {},
      });

      if (error) {
        const errorData = data || {};
        setResult({
          success: false,
          error: error.message,
          details: error,
          responseData: errorData
        });
      } else {
        setResult({
          success: true,
          data
        });
      }
    } catch (err) {
      setResult({
        success: false,
        error: err instanceof Error ? err.message : 'Unknown error'
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-3 mb-4">
        <Mail className="w-6 h-6 text-cyan-500" />
        <h2 className="text-xl font-semibold text-gray-900">Test Reminder Emails</h2>
      </div>

      <p className="text-gray-600 mb-4">
        This will check for users who haven't completed onboarding and send them reminder emails.
      </p>

      <button
        onClick={testReminderFunction}
        disabled={testing}
        className="bg-cyan-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
      >
        {testing ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Sending Reminders...
          </>
        ) : (
          <>
            <Mail className="w-5 h-5" />
            Send Reminder Emails
          </>
        )}
      </button>

      {result && (
        <div className={`mt-4 p-4 rounded-lg ${result.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
          <h3 className={`font-semibold mb-2 ${result.success ? 'text-green-900' : 'text-red-900'}`}>
            {result.success ? 'Success' : 'Error'}
          </h3>
          <pre className="text-sm overflow-auto bg-white p-3 rounded border text-gray-900">
            {JSON.stringify(result, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}
